import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";


import ResearchCTA from "./ResearchCTA";

const METHODOLOGY_SLUG = "ai-visibility-score-methodology";

export default function ResearchMethodology() {
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/research/publications/${METHODOLOGY_SLUG}`);
        setPub(data);
      } catch {
        // fallback to static content rendered below
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="research-methodology-page">
      <Helmet>
        <title>The AI Visibility Score™ — Methodology</title>
        <meta name="description" content="How The Entity Agency measures AI visibility. Five dimensions, four platforms, twenty prompts per audit." />
        <link rel="canonical" href="https://theentityagency.com/research/methodology" />
      </Helmet>


      <section className="border-b border-[#2a2620]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
          <nav className="font-mono-eav text-[11px] tracking-[0.2em] uppercase text-[#8a7e6d]">
            <Link to="/research" className="hover:text-[#b8963e]">Research</Link>
            <span className="mx-2">/</span>
            <span className="text-[#b8963e]">Methodology</span>
          </nav>
          <p className="mt-6 font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
            Methodology
          </p>
          <h1 className="mt-2 font-serif-display text-5xl text-[#f4efe6] leading-tight"
              style={{ fontWeight: 500 }}>
            The AI Visibility Score™
          </h1>
          <p className="mt-4 text-lg text-[#c9bca4]">
            How we measure how AI systems perceive, describe, and recommend a business.
          </p>
        </div>
      </section>

      <article>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
          {loading ? (
            <p className="text-[#8a7e6d]">Loading…</p>
          ) : pub?.body_html ? (
            <div className="research-body text-[#c9bca4]"
                 data-testid="research-methodology-body"
                 dangerouslySetInnerHTML={{ __html: pub.body_html }} />
          ) : (
            <StaticMethodology />
          )}
        </div>
      </article>

      <ResearchCTA />

    </div>
  );
}


function StaticMethodology() {
  return (
    <div className="research-body text-[#c9bca4] space-y-6">
      <p>The AI Visibility Score™ is a 0-100 composite of five dimensions that together capture how a business is recognized, categorized, described, and recommended by AI systems.</p>
      <h2>The five dimensions</h2>
      <p>Each scored 0-20 with the question it answers:</p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Entity Recognition</strong> (0-20) — Can AI accurately identify who the company is?</li>
        <li><strong>AI Discovery Presence</strong> (0-20) — Can AI discover and recommend the company?</li>
        <li><strong>Competitive Positioning</strong> (0-20) — Can AI compare the company against competitors?</li>
        <li><strong>Accuracy &amp; Consistency</strong> (0-20) — Are descriptions correct and consistent?</li>
        <li><strong>Authority &amp; Confidence</strong> (0-20) — Does AI have enough evidence to speak confidently?</li>
      </ol>
      <h2>Platforms tested</h2>
      <p>Every audit runs against ChatGPT, Claude, Gemini, and Perplexity.</p>
    </div>
  );
}
